// src/components/AdminPanel.js
import { useEffect, useState } from "react";
import axios from "axios";

import API_BASE from "../api";

export default function AdminPanel({ lang }) {
  const [tab, setTab] = useState("content"); // 'content' | 'products'

  const [products, setProducts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [texts, setTexts] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name_pl: "",
    name_en: "",
    desc_pl: "",
    desc_en: "",
    price: "",
    priceEUR: "",
    images: [],
    videos: [],
    contact: "contact@example.com",
  });

  // pliki do produktu (nowe uploady)
  const [productFiles, setProductFiles] = useState([]);
  const [productVideoFiles, setProductVideoFiles] = useState([]);
  // plik do galerii
  const [galleryFile, setGalleryFile] = useState(null);

  // index przeciąganego elementu w galerii
  const [dragIndex, setDragIndex] = useState(null);

  // ---------------- ŁADOWANIE DANYCH ----------------

  const loadProducts = () => {
    axios
      .get(API_BASE + "/api/products")
      .then((res) => setProducts(res.data || []))
      .catch((err) => console.error("loadProducts error:", err));
  };

  const loadGallery = () => {
    axios
      .get(API_BASE + "/api/gallery")
      .then((res) => setGallery(res.data || []))
      .catch((err) => console.error("loadGallery error:", err));
  };

  const loadTexts = () => {
    axios
      .get(API_BASE + "/api/texts")
      .then((res) => setTexts(res.data || {}))
      .catch((err) => console.error("loadTexts error:", err));
  };

  useEffect(() => {
    loadProducts();
    loadGallery();
    loadTexts();
  }, []);

  // ---------------- UPLOAD ZDJĘĆ / VIDEO ----------------

  const uploadProductImages = async (files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("images", f));

    const res = await axios.post(API_BASE + "/api/upload-product-images", fd);
    return res.data.filenames || [];
  };

  const uploadProductVideos = async (files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("videos", f));

    const res = await axios.post(API_BASE + "/api/upload-product-videos", fd);
    return res.data.filenames || [];
  };

  const uploadGalleryImage = async (file) => {
    const fd = new FormData();
    fd.append("image", file);
    const res = await axios.post(API_BASE + "/api/gallery", fd);
    return res.data.filename;
  };

  // ---------------- ZAPIS PRODUKTU ----------------

  const handleProductSubmit = async () => {
    try {
      const namePl = (form.name_pl || "").trim();
      const nameEn = (form.name_en || "").trim();
      const price = (form.price || "").trim();

      if (!namePl && !nameEn) {
        alert(
          lang === "pl"
            ? "Podaj nazwę produktu (PL lub EN)."
            : "Please provide product name (PL or EN)."
        );
        return;
      }

      if (!price) {
        alert(
          lang === "pl"
            ? "Podaj cenę produktu."
            : "Please provide product price."
        );
        return;
      }

      let images = Array.isArray(form.images) ? [...form.images] : [];
      let videos = Array.isArray(form.videos) ? [...form.videos] : [];

      const maxImages = 10;
      const maxVideos = 3;

      // upload nowych ZDJĘĆ jeśli są
      if (productFiles.length > 0 && images.length < maxImages) {
        const freeSlots = maxImages - images.length;
        const filesToUpload = productFiles.slice(0, freeSlots);

        if (filesToUpload.length > 0) {
          const uploaded = await uploadProductImages(filesToUpload);
          images = images.concat(uploaded);
        }
      }

      // upload nowych VIDEO jeśli są
      if (productVideoFiles.length > 0 && videos.length < maxVideos) {
        const freeVideoSlots = maxVideos - videos.length;
        const videosToUpload = productVideoFiles.slice(0, freeVideoSlots);

        if (videosToUpload.length > 0) {
          const uploadedVideos = await uploadProductVideos(videosToUpload);
          videos = videos.concat(uploadedVideos);
        }
      }

      if (images.length === 0) {
        alert(
          lang === "pl"
            ? "Dodaj co najmniej jedno zdjęcie produktu."
            : "Please add at least one product image."
        );
        return;
      }

      images = images.slice(0, maxImages);
      videos = videos.slice(0, maxVideos);

      const id = editingId || String(Date.now());

      const payload = {
        id,
        name_pl: namePl,
        name_en: nameEn,
        desc_pl: form.desc_pl || "",
        desc_en: form.desc_en || "",
        price,
        priceEUR: (form.priceEUR || "").trim(),
        images,
        image: images[0], // dla kompatybilności
        videos,
        contact: form.contact || "contact@example.com",
      };

      if (editingId) {
        await axios.put(API_BASE + "/api/products/" + id, payload);
      } else {
        await axios.post(API_BASE + "/api/products", payload);
      }

      // reset formularza
      setForm({
        name_pl: "",
        name_en: "",
        desc_pl: "",
        desc_en: "",
        price: "",
        priceEUR: "",
        images: [],
        videos: [],
        contact: "contact@example.com",
      });
      setProductFiles([]);
      setProductVideoFiles([]);
      setEditingId(null);
      loadProducts();

      alert(lang === "pl" ? "Zapisano produkt" : "Product saved");
    } catch (e) {
      console.error("handleProductSubmit error:", e);
      alert("Error saving product");
    }
  };

  const handleEditProduct = (p) => {
    setEditingId(p.id);
    setForm({
      name_pl: p.name_pl || "",
      name_en: p.name_en || "",
      desc_pl: p.desc_pl || "",
      desc_en: p.desc_en || "",
      price: p.price || "",
      priceEUR: p.priceEUR || "",
      images: Array.isArray(p.images)
        ? p.images.slice(0, 10)
        : p.image
        ? [p.image]
        : [],
      videos: Array.isArray(p.videos) ? p.videos : [],
      contact: p.contact || "contact@example.com",
    });
    setProductFiles([]);
    setProductVideoFiles([]);
  };

  const handleDeleteProduct = async (id) => {
    if (
      !window.confirm(
        lang === "pl" ? "Usunąć produkt?" : "Delete this product?"
      )
    ) {
      return;
    }

    try {
      await axios.delete(API_BASE + "/api/products/" + id);
      loadProducts();
    } catch (e) {
      console.error("handleDeleteProduct error:", e);
      alert("Error deleting product");
    }
  };

  // ---------------- GALERIA ----------------

  const handleGalleryUpload = async () => {
    if (!galleryFile) return;

    try {
      await uploadGalleryImage(galleryFile);
      setGalleryFile(null);
      loadGallery();
      alert(
        lang === "pl"
          ? "Zdjęcie dodane do galerii"
          : "Image added to gallery"
      );
    } catch (e) {
      console.error("handleGalleryUpload error:", e);
      alert("Upload error");
    }
  };

  const handleDeleteGalleryImage = async (file) => {
    if (!window.confirm(`Usunąć zdjęcie ${file}?`)) return;
    try {
      await axios.delete(API_BASE + "/api/gallery/" + file);
      loadGallery();
    } catch (e) {
      console.error("delete gallery error:", e);
      alert("Błąd usuwania zdjęcia");
    }
  };

  // ---------------- DRAG & DROP W GALERII (FRONT) ----------------

  const handleGalleryDrop = (fromIndex, toIndex) => {
    if (
      fromIndex === null ||
      toIndex === null ||
      fromIndex === toIndex
    ) {
      return;
    }

    setGallery((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  // ---------------- ZAPIS KOLEJNOŚCI (BACKEND) ----------------

  const handleSaveGalleryOrder = async () => {
    try {
      // wysyłamy tablicę nazw plików, np. ["123_a.jpg","456_b.jpg",...]
      await axios.post(API_BASE + "/api/gallery-order", gallery);
      alert(
        lang === "pl"
          ? "Zapisano kolejność galerii"
          : "Gallery order saved"
      );
    } catch (e) {
      console.error("save gallery order error:", e);
      alert(
        lang === "pl"
          ? "Błąd zapisu kolejności galerii"
          : "Error saving gallery order"
      );
    }
  };

  // ---------------- TEKSTY STRONY ----------------

  const handleTextsChange = (section, langKey, field, value) => {
    setTexts((prev) => ({
      ...prev,
      [section]: {
        ...(prev?.[section] || {}),
        [langKey]: {
          ...(prev?.[section]?.[langKey] || {}),
          [field]: value,
        },
      },
    }));
  };

  const handleSaveTexts = async () => {
    try {
      await axios.post(API_BASE + "/api/texts", texts);
      alert(lang === "pl" ? "Zapisano teksty" : "Texts saved");
    } catch (e) {
      console.error("handleSaveTexts error:", e);
      alert("Error saving texts");
    }
  };

  if (!texts) return <section className="section">Ładowanie...</section>;

  const tHomePL = texts.home?.pl || {};
  const tHomeEN = texts.home?.en || {};
  const tAboutPL = texts.about?.pl || {};
  const tAboutEN = texts.about?.en || {};
  const tGalleryPL = texts.gallery?.pl || {};
  const tGalleryEN = texts.gallery?.en || {};
  const tShopPL = texts.shop?.pl || {};
  const tShopEN = texts.shop?.en || {};
  const tContactPL = texts.contact?.pl || {};
  const tContactEN = texts.contact?.en || {};

  return (
    <section className="section">
      <div className="section-header">
        <h2 className="section-title">Admin</h2>
        <span className="section-subtitle">
          {lang === "pl"
            ? "Zarządzanie treścią strony i produktami"
            : "Manage site content and products"}
        </span>
      </div>

      <div className="admin-tabs">
        <button
          className={"admin-tab-btn " + (tab === "content" ? "active" : "")}
          onClick={() => setTab("content")}
        >
          {lang === "pl" ? "Teksty strony" : "Site texts"}
        </button>
        <button
          className={"admin-tab-btn " + (tab === "products" ? "active" : "")}
          onClick={() => setTab("products")}
        >
          {lang === "pl" ? "Produkty i galeria" : "Products & gallery"}
        </button>
      </div>

      {/* --------------- TEKSTY STRONY --------------- */}

      {tab === "content" && (
        <div className="admin-panel">
          <h3>{lang === "pl" ? "Strona główna (Home)" : "Home page"}</h3>

          {/* HOME PL */}
          <div className="admin-field">
            <label>Home title (PL)</label>
            <input
              value={tHomePL.title || ""}
              onChange={(e) =>
                handleTextsChange("home", "pl", "title", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Home subtitle (PL)</label>
            <textarea
              rows={3}
              value={tHomePL.subtitle || ""}
              onChange={(e) =>
                handleTextsChange("home", "pl", "subtitle", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Przycisk galeria (PL)</label>
            <input
              value={tHomePL.btnGallery || ""}
              onChange={(e) =>
                handleTextsChange("home", "pl", "btnGallery", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Przycisk kontakt (PL)</label>
            <input
              value={tHomePL.btnContact || ""}
              onChange={(e) =>
                handleTextsChange("home", "pl", "btnContact", e.target.value)
              }
            />
          </div>

          {/* HOME EN */}
          <div style={{ height: 16 }} />
          <div className="admin-field">
            <label>Home title (EN)</label>
            <input
              value={tHomeEN.title || ""}
              onChange={(e) =>
                handleTextsChange("home", "en", "title", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Home subtitle (EN)</label>
            <textarea
              rows={3}
              value={tHomeEN.subtitle || ""}
              onChange={(e) =>
                handleTextsChange("home", "en", "subtitle", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Button gallery (EN)</label>
            <input
              value={tHomeEN.btnGallery || ""}
              onChange={(e) =>
                handleTextsChange("home", "en", "btnGallery", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Button contact (EN)</label>
            <input
              value={tHomeEN.btnContact || ""}
              onChange={(e) =>
                handleTextsChange("home", "en", "btnContact", e.target.value)
              }
            />
          </div>

          <hr style={{ margin: "18px 0" }} />

          {/* ABOUT */}
          <h3>{lang === "pl" ? "O nas (About)" : "About"}</h3>

          <div className="admin-field">
            <label>O nas tytuł (PL)</label>
            <input
              value={tAboutPL.title || ""}
              onChange={(e) =>
                handleTextsChange("about", "pl", "title", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>O nas podtytuł (PL)</label>
            <input
              value={tAboutPL.subtitle || ""}
              onChange={(e) =>
                handleTextsChange("about", "pl", "subtitle", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>O nas treść (PL)</label>
            <textarea
              rows={5}
              value={tAboutPL.body || ""}
              onChange={(e) =>
                handleTextsChange("about", "pl", "body", e.target.value)
              }
            />
          </div>

          <div className="admin-field">
            <label>About title (EN)</label>
            <input
              value={tAboutEN.title || ""}
              onChange={(e) =>
                handleTextsChange("about", "en", "title", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>About subtitle (EN)</label>
            <input
              value={tAboutEN.subtitle || ""}
              onChange={(e) =>
                handleTextsChange("about", "en", "subtitle", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>About body (EN)</label>
            <textarea
              rows={5}
              value={tAboutEN.body || ""}
              onChange={(e) =>
                handleTextsChange("about", "en", "body", e.target.value)
              }
            />
          </div>

          <hr style={{ margin: "18px 0" }} />

          {/* GALLERY TEXTS */}
          <h3>{lang === "pl" ? "Galeria" : "Gallery"}</h3>
          <div className="admin-field">
            <label>Galeria tytuł (PL)</label>
            <input
              value={tGalleryPL.title || ""}
              onChange={(e) =>
                handleTextsChange("gallery", "pl", "title", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Galeria podtytuł (PL)</label>
            <input
              value={tGalleryPL.subtitle || ""}
              onChange={(e) =>
                handleTextsChange("gallery", "pl", "subtitle", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Galeria pusty komunikat (PL)</label>
            <textarea
              rows={2}
              value={tGalleryPL.empty || ""}
              onChange={(e) =>
                handleTextsChange("gallery", "pl", "empty", e.target.value)
              }
            />
          </div>

          <div className="admin-field">
            <label>Gallery title (EN)</label>
            <input
              value={tGalleryEN.title || ""}
              onChange={(e) =>
                handleTextsChange("gallery", "en", "title", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Gallery subtitle (EN)</label>
            <input
              value={tGalleryEN.subtitle || ""}
              onChange={(e) =>
                handleTextsChange("gallery", "en", "subtitle", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Gallery empty message (EN)</label>
            <textarea
              rows={2}
              value={tGalleryEN.empty || ""}
              onChange={(e) =>
                handleTextsChange("gallery", "en", "empty", e.target.value)
              }
            />
          </div>

          <hr style={{ margin: "18px 0" }} />

          {/* SHOP TEXTS */}
          <h3>{lang === "pl" ? "Sklep" : "Shop"}</h3>
          <div className="admin-field">
            <label>Sklep tytuł (PL)</label>
            <input
              value={tShopPL.title || ""}
              onChange={(e) =>
                handleTextsChange("shop", "pl", "title", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Sklep podtytuł (PL)</label>
            <input
              value={tShopPL.subtitle || ""}
              onChange={(e) =>
                handleTextsChange("shop", "pl", "subtitle", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Sklep pusty komunikat (PL)</label>
            <textarea
              rows={2}
              value={tShopPL.empty || ""}
              onChange={(e) =>
                handleTextsChange("shop", "pl", "empty", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Przycisk (PL)</label>
            <input
              value={tShopPL.btn || ""}
              onChange={(e) =>
                handleTextsChange("shop", "pl", "btn", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Początek tematu maila (PL)</label>
            <input
              value={tShopPL.inquiryPrefix || ""}
              onChange={(e) =>
                handleTextsChange(
                  "shop",
                  "pl",
                  "inquiryPrefix",
                  e.target.value
                )
              }
            />
          </div>

          <div className="admin-field">
            <label>Shop title (EN)</label>
            <input
              value={tShopEN.title || ""}
              onChange={(e) =>
                handleTextsChange("shop", "en", "title", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Shop subtitle (EN)</label>
            <input
              value={tShopEN.subtitle || ""}
              onChange={(e) =>
                handleTextsChange("shop", "en", "subtitle", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Shop empty message (EN)</label>
            <textarea
              rows={2}
              value={tShopEN.empty || ""}
              onChange={(e) =>
                handleTextsChange("shop", "en", "empty", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Button (EN)</label>
            <input
              value={tShopEN.btn || ""}
              onChange={(e) =>
                handleTextsChange("shop", "en", "btn", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Inquiry prefix (EN)</label>
            <input
              value={tShopEN.inquiryPrefix || ""}
              onChange={(e) =>
                handleTextsChange(
                  "shop",
                  "en",
                  "inquiryPrefix",
                  e.target.value
                )
              }
            />
          </div>

          <hr style={{ margin: "18px 0" }} />

          {/* CONTACT TEXTS */}
          <h3>{lang === "pl" ? "Kontakt" : "Contact"}</h3>
          <div className="admin-field">
            <label>Kontakt tytuł (PL)</label>
            <input
              value={tContactPL.title || ""}
              onChange={(e) =>
                handleTextsChange("contact", "pl", "title", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Kontakt podtytuł (PL)</label>
            <input
              value={tContactPL.subtitle || ""}
              onChange={(e) =>
                handleTextsChange("contact", "pl", "subtitle", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Etykieta email (PL)</label>
            <input
              value={tContactPL.emailLabel || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "pl",
                  "emailLabel",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Email (PL)</label>
            <input
              value={tContactPL.emailValue || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "pl",
                  "emailValue",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Etykieta lokalizacja (PL)</label>
            <input
              value={tContactPL.locationLabel || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "pl",
                  "locationLabel",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Lokalizacja (PL)</label>
            <input
              value={tContactPL.locationValue || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "pl",
                  "locationValue",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Etykieta Instagram (PL)</label>
            <input
              value={tContactPL.instagramLabel || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "pl",
                  "instagramLabel",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Instagram (PL)</label>
            <input
              value={tContactPL.instagramValue || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "pl",
                  "instagramValue",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Etykieta telefonu (PL)</label>
            <input
              value={tContactPL.phoneLabel || ""}
              onChange={(e) =>
                handleTextsChange("contact", "pl", "phoneLabel", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Telefon (PL)</label>
            <input
              value={tContactPL.phoneValue || ""}
              onChange={(e) =>
                handleTextsChange("contact", "pl", "phoneValue", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Etykieta Facebook (PL)</label>
            <input
              value={tContactPL.facebookLabel || ""}
              onChange={(e) =>
                handleTextsChange("contact", "pl", "facebookLabel", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Adres URL Facebook (PL)</label>
            <input
              value={tContactPL.facebookValue || ""}
              onChange={(e) =>
                handleTextsChange("contact", "pl", "facebookValue", e.target.value)
              }
            />
          </div>

          <div className="admin-field">
            <label>Contact title (EN)</label>
            <input
              value={tContactEN.title || ""}
              onChange={(e) =>
                handleTextsChange("contact", "en", "title", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Contact subtitle (EN)</label>
            <input
              value={tContactEN.subtitle || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "en",
                  "subtitle",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Email label (EN)</label>
            <input
              value={tContactEN.emailLabel || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "en",
                  "emailLabel",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Email (EN)</label>
            <input
              value={tContactEN.emailValue || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "en",
                  "emailValue",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Location label (EN)</label>
            <input
              value={tContactEN.locationLabel || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "en",
                  "locationLabel",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Location (EN)</label>
            <input
              value={tContactEN.locationValue || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "en",
                  "locationValue",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Instagram label (EN)</label>
            <input
              value={tContactEN.instagramLabel || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "en",
                  "instagramLabel",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Instagram (EN)</label>
            <input
              value={tContactEN.instagramValue || ""}
              onChange={(e) =>
                handleTextsChange(
                  "contact",
                  "en",
                  "instagramValue",
                  e.target.value
                )
              }
            />
          </div>
          <div className="admin-field">
            <label>Phone label (EN)</label>
            <input
              value={tContactEN.phoneLabel || ""}
              onChange={(e) =>
                handleTextsChange("contact", "en", "phoneLabel", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Phone (EN)</label>
            <input
              value={tContactEN.phoneValue || ""}
              onChange={(e) =>
                handleTextsChange("contact", "en", "phoneValue", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Facebook label (EN)</label>
            <input
              value={tContactEN.facebookLabel || ""}
              onChange={(e) =>
                handleTextsChange("contact", "en", "facebookLabel", e.target.value)
              }
            />
          </div>
          <div className="admin-field">
            <label>Facebook URL (EN)</label>
            <input
              value={tContactEN.facebookValue || ""}
              onChange={(e) =>
                handleTextsChange("contact", "en", "facebookValue", e.target.value)
              }
            />
          </div>

          <button className="btn-primary" onClick={handleSaveTexts}>
            {lang === "pl" ? "Zapisz teksty" : "Save texts"}
          </button>
        </div>
      )}

      {/* --------------- PRODUKTY + GALERIA --------------- */}

      {tab === "products" && (
        <div className="admin-layout">
          {/* LEWA – PRODUKT */}
          <div className="admin-panel">
            <h3>
              {lang === "pl"
                ? "Nowy / edycja produktu"
                : "New / edit product"}
            </h3>

            <div className="admin-field">
              <label>Nazwa (PL)</label>
              <input
                value={form.name_pl}
                onChange={(e) =>
                  setForm({ ...form, name_pl: e.target.value })
                }
              />
            </div>

            <div className="admin-field">
              <label>Name (EN)</label>
              <input
                value={form.name_en}
                onChange={(e) =>
                  setForm({ ...form, name_en: e.target.value })
                }
              />
            </div>

            <div className="admin-field">
              <label>Opis (PL)</label>
              <textarea
                rows={3}
                value={form.desc_pl}
                onChange={(e) =>
                  setForm({ ...form, desc_pl: e.target.value })
                }
              />
            </div>

            <div className="admin-field">
              <label>Description (EN)</label>
              <textarea
                rows={3}
                value={form.desc_en}
                onChange={(e) =>
                  setForm({ ...form, desc_en: e.target.value })
                }
              />
            </div>

            <div className="admin-field">
              <label>
                {lang === "pl" ? "Cena w PLN (np. 950)" : "Price in PLN (e.g. 950)"}
              </label>
              <input
                value={form.price}
                onChange={(e) =>
                  setForm({ ...form, price: e.target.value })
                }
              />
            </div>

            <div className="admin-field">
              <label>
                {lang === "pl"
                  ? "Cena w EUR (np. 133) — dla wersji EN"
                  : "Price in EUR (e.g. 133) — for EN version"}
              </label>
              <input
                value={form.priceEUR}
                onChange={(e) =>
                  setForm({ ...form, priceEUR: e.target.value })
                }
              />
            </div>

            <div className="admin-field">
              <label>Email kontaktowy</label>
              <input
                value={form.contact}
                onChange={(e) =>
                  setForm({ ...form, contact: e.target.value })
                }
              />
            </div>

            {/* ZDJĘCIA PRODUKTU */}
            <div className="admin-field">
              <label>
                {lang === "pl"
                  ? "Zdjęcia produktu (max 10)"
                  : "Product images (max 10)"}
              </label>

              {/* input do ładowania nowych plików */}
              <input
                type="file"
                multiple
                onChange={(e) =>
                  setProductFiles(Array.from(e.target.files || []))
                }
              />

              {/* miniatury aktualnych zdjęć z możliwością usuwania */}
              {form.images?.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginTop: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  {form.images.map((img) => (
                    <div
                      key={img}
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        padding: "4px",
                        textAlign: "center",
                        width: "100px",
                      }}
                    >
                      <img
                        src={`${API_BASE}/images/products/${img}`}
                        alt=""
                        style={{
                          width: "90px",
                          height: "90px",
                          objectFit: "cover",
                          borderRadius: "4px",
                          marginBottom: "4px",
                        }}
                      />

                      <button
                        className="admin-small-btn"
                        onClick={() => {
                          const updated = form.images.filter(
                            (x) => x !== img
                          );
                          setForm({ ...form, images: updated });
                        }}
                        style={{ width: "100%", fontSize: "11px" }}
                      >
                        {lang === "pl" ? "Usuń" : "Remove"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* VIDEO PRODUKTU */}
            <div className="admin-field">
              <label>
                {lang === "pl"
                  ? "Wideo produktu (max 3, mp4/webm)"
                  : "Product videos (max 3, mp4/webm)"}
              </label>

              <input
                type="file"
                multiple
                accept="video/mp4,video/webm,video/quicktime"
                onChange={(e) =>
                  setProductVideoFiles(Array.from(e.target.files || []))
                }
              />

              {form.videos?.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    marginTop: "10px",
                  }}
                >
                  {form.videos.map((vid) => (
                    <div
                      key={vid}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        padding: "6px 8px",
                        fontSize: "12px",
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "180px",
                        }}
                      >
                        🎥 {vid}
                      </span>
                      <button
                        className="admin-small-btn"
                        onClick={() => {
                          const updated = form.videos.filter(
                            (x) => x !== vid
                          );
                          setForm({ ...form, videos: updated });
                        }}
                        style={{ fontSize: "11px" }}
                      >
                        {lang === "pl" ? "Usuń" : "Remove"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button className="btn-primary" onClick={handleProductSubmit}>
              {editingId
                ? lang === "pl"
                  ? "Zapisz zmiany"
                  : "Save changes"
                : lang === "pl"
                ? "Dodaj produkt"
                : "Add product"}
            </button>

            <hr style={{ margin: "18px 0" }} />

            {/* GALERIA – UPLOAD */}
            <h3>
              {lang === "pl"
                ? "Galeria – dodaj zdjęcie"
                : "Gallery – add image"}
            </h3>
            <div className="admin-field">
              <label>
                {lang === "pl" ? "Plik do galerii" : "File for gallery"}
              </label>
              <input
                type="file"
                onChange={(e) =>
                  setGalleryFile(e.target.files[0] || null)
                }
              />
            </div>
            <button className="btn-ghost" onClick={handleGalleryUpload}>
              {lang === "pl" ? "Dodaj do galerii" : "Add to gallery"}
            </button>
          </div>

          {/* PRAWA – LISTY: PRODUKTY + GALERIA */}
          <div className="admin-list">
            <h3>{lang === "pl" ? "Produkty" : "Products"}</h3>
            {products.map((p) => (
              <div key={p.id} className="admin-list-item">
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14 }}>
                    {p.name_pl} {p.price ? `• ${p.price} zł` : ""}
                  </div>

                  {/* MINIATURY PRODUKTU W LISTINGU */}
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      marginTop: "6px",
                      flexWrap: "wrap",
                    }}
                  >
                    {(p.images || []).map((img) => (
                      <img
                        key={img}
                        src={`${API_BASE}/images/products/${img}`}
                        alt=""
                        style={{
                          width: "45px",
                          height: "45px",
                          objectFit: "cover",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                        }}
                      />
                    ))}
                    {(p.videos || []).map((vid) => (
                      <div
                        key={vid}
                        style={{
                          width: "45px",
                          height: "45px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#000",
                          color: "#fff",
                          fontSize: "18px",
                        }}
                      >
                        ▶
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    className="admin-small-btn"
                    onClick={() => handleEditProduct(p)}
                  >
                    {lang === "pl" ? "Edytuj" : "Edit"}
                  </button>
                  <button
                    className="admin-small-btn"
                    onClick={() => handleDeleteProduct(p.id)}
                  >
                    {lang === "pl" ? "Usuń" : "Delete"}
                  </button>
                </div>
              </div>
            ))}

            <hr
              style={{
                margin: "16px 0",
                borderColor: "var(--border-subtle)",
              }}
            />

            <h3>
              {lang === "pl"
                ? "Galeria – miniaturki (przeciągnij, aby zmienić kolejność)"
                : "Gallery – thumbnails (drag to reorder)"}
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, 90px)",
                gap: "10px",
              }}
            >
              {gallery.map((g, index) => (
                <div
                  key={g}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleGalleryDrop(dragIndex, index);
                    setDragIndex(null);
                  }}
                  style={{
                    border: "1px solid #ddd",
                    padding: "4px",
                    borderRadius: "4px",
                    textAlign: "center",
                    background:
                      dragIndex === index ? "rgba(0,0,0,0.05)" : "white",
                    cursor: "move",
                  }}
                >
                  <img
                    src={API_BASE + "/images/gallery/" + g}
                    alt={g}
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      display: "block",
                      margin: "0 auto 4px",
                    }}
                  />
                  <div
                    style={{
                      fontSize: 11,
                      opacity: 0.7,
                      marginBottom: 4,
                    }}
                  >
                    ⇅
                  </div>
                  <button
                    className="admin-small-btn"
                    onClick={() => handleDeleteGalleryImage(g)}
                    style={{ fontSize: "11px" }}
                  >
                    {lang === "pl" ? "Usuń" : "Delete"}
                  </button>
                </div>
              ))}
            </div>

            {gallery.length > 1 && (
              <div style={{ marginTop: 12 }}>
                <button
                  className="btn-primary"
                  onClick={handleSaveGalleryOrder}
                >
                  {lang === "pl"
                    ? "Zapisz kolejność galerii"
                    : "Save gallery order"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
